import { CHART_TYPE_AREA, CHART_TYPE_BARS, CHART_TYPE_HEIKIN_ASHI } from "../../constants";
import { createRectRenderer } from "../gl/rectRenderer";
import { createLineRenderer } from "../gl/lineRenderer";
import { createBandRenderer } from "../gl/bandRenderer";
import { buildCandleInstances, RECTS_PER_CANDLE } from "./candles";
import { buildBarInstances, RECTS_PER_BAR } from "./ohlcBars";
import { buildHeikinAshi, heikinAshiBar } from "./heikinAshi";
import { buildLineAreaInstances } from "./lineArea";

const EMPTY = new Float32Array(0);
const NO_LINE_AREA = { band: EMPTY, line: EMPTY };

/**
 * GPU buffers of the main series. `build` fills them for all bars (a new
 * history, a new bar or another type), `updateLast` rewrites only the last
 * bar (a realtime tick). Prices are relative to `priceBase`, so float32
 * keeps their precision (e.g. BTC 80,000.01); the base is the last close at
 * the last build and stays put between ticks. Heikin Ashi bars are computed
 * here and kept for the autoscale and the colour of the last bar.
 *
 * @param {number[]} lineColor - RGBA 0..1 of the Line chart
 */
export const createSeriesBuffers = (lineColor) => {
  const rects = createRectRenderer();
  const band = createBandRenderer();
  const line = createLineRenderer();
  let priceBase = 0;
  let heikinAshiBars = [];

  const buildRects = (type, tail, firstIndex) => {
    const build = type === CHART_TYPE_BARS ? buildBarInstances : buildCandleInstances;

    return build(tail, priceBase, rects.floatsPerInstance, firstIndex);
  };

  const buildLineArea = (tail, firstIndex) =>
    buildLineAreaInstances(tail, priceBase, {
      firstIndex,
      color: lineColor,
      floatsPerLine: line.floatsPerInstance,
    });

  const drawnBars = (type, bars) => (type === CHART_TYPE_HEIKIN_ASHI ? heikinAshiBars : bars);

  return {
    renderers: { rects, band, line },

    get priceBase() {
      return priceBase;
    },

    /**
     * Bars the rects show: Heikin Ashi bars on Heikin Ashi, `bars` otherwise.
     *
     * @param {number} type - CHART_TYPE_* value
     * @param {object[]} bars
     */
    drawnBars,

    /**
     * @param {number} type - CHART_TYPE_* value
     * @param {object[]} bars - all bars, oldest first
     */
    build: (type, bars) => {
      const isLine = type === CHART_TYPE_AREA;

      priceBase = bars.length ? bars[bars.length - 1].close : 0;
      heikinAshiBars = type === CHART_TYPE_HEIKIN_ASHI ? buildHeikinAshi(bars) : [];

      const lineArea = isLine ? buildLineArea(bars, 0) : NO_LINE_AREA;

      rects.setInstances(isLine ? EMPTY : buildRects(type, drawnBars(type, bars), 0));
      band.setInstances(lineArea.band);
      line.setInstances(lineArea.line);
    },

    /**
     * @param {number} type - CHART_TYPE_* value of the last build
     * @param {object[]} bars - the same bars as in the last build, the last one changed
     */
    updateLast: (type, bars) => {
      const lastIndex = bars.length - 1;

      if (type === CHART_TYPE_AREA) {
        if (lastIndex < 1) return;

        const lineArea = buildLineArea(bars.slice(lastIndex - 1), lastIndex - 1);

        band.updateInstances(lastIndex - 1, lineArea.band);
        line.updateInstances(lastIndex - 1, lineArea.line);
        return;
      }

      if (type === CHART_TYPE_HEIKIN_ASHI) {
        heikinAshiBars[lastIndex] = heikinAshiBar(bars[lastIndex], heikinAshiBars[lastIndex - 1]);
      }

      const rectsPerBar = type === CHART_TYPE_BARS ? RECTS_PER_BAR : RECTS_PER_CANDLE;

      rects.updateInstances(
        lastIndex * rectsPerBar,
        buildRects(type, [drawnBars(type, bars)[lastIndex]], lastIndex),
      );
    },
  };
};
